/**
 * Admin Portal → Codes → Overview: "Codes reconcile" — the last run of the nightly codes reconcile
 * and "Run reconcile now" (Codes PR-9; routers/admin_codes_reconcile.py).
 *
 * Every admin role reads the last run. The button shows for admin roles only, and only once the role
 * is known (fail closed) — the server checks the role again (require_role) and answers 403 to anyone
 * else. A run happens in the background: the panel polls while one is in flight, then shows its
 * result. Findings land in the Alert Center under Business.
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdminCard } from '../../../components/admin/ui';
import { PartnerApiError } from '../../../services/partnersAdminService';
import { hint } from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import {
  RECONCILE_POLL_MS,
  checkLine,
  reconcileSummaryLine,
  reconcileTone,
  runReconcileError,
  shouldPoll,
  type ReconcileRuns,
} from './codesReconcileLogic';

const TONE_COLOR: Record<string, string> = {
  success: 'var(--status-success-text, #166534)',
  warning: 'var(--status-warning-text, #92400e)',
  error: 'var(--status-error-text, #B91C1C)',
  info: 'var(--text-secondary, #6b7280)',
};

export const CodesReconcilePanel: React.FC<{ writable: boolean }> = ({ writable }) => {
  const [runs, setRuns] = useState<ReconcileRuns | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const polls = useRef(0);
  const live = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await CodesAdmin.reconcileRuns();
      if (!live.current) return;
      setRuns(r);
      setLoadError(null);
      if (shouldPoll(r, polls.current)) {
        polls.current += 1;
        timer.current = setTimeout(() => { void load(); }, RECONCILE_POLL_MS);
      }
    } catch (e) {
      if (live.current) setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    live.current = true;
    void load();
    return () => {
      live.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  const start = async () => {
    setStarting(true);
    setMessage(null);
    try {
      await CodesAdmin.runReconcile();
      setMessage('Reconcile started. Its result appears here in a minute or two.');
      polls.current = 0;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => { void load(); }, 1500);
    } catch (e) {
      setMessage(e instanceof PartnerApiError ? runReconcileError(e.status, e.code, e.message) : String(e));
    } finally {
      setStarting(false);
    }
  };

  const last = runs?.last ?? null;
  const disabled = runs?.switches?.codes_reconcile === false;
  const running = Boolean(runs?.running);
  return (
    <AdminCard style={{ marginTop: 16 }}>
      <div data-testid="reconcile-panel" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px' }}>
          <h2 style={{ fontSize: 16, margin: '0 0 6px' }}>Codes reconcile</h2>
          <p style={{ ...hint, margin: '0 0 8px' }}>
            Every night Coach Cedar checks every code family: code names, use counters, App Store batches, the
            stores&apos; own offers, tag discounts, Summer50 reward stock, the quarter&apos;s quota and inferred App
            Store redemptions. What it finds lands in the Alert Center under Business.
          </p>
          {loadError && <p role="alert" style={{ ...hint, color: TONE_COLOR.error }}>{loadError}</p>}
          {runs?.schema_pending && <p style={hint}>Not set up yet — the deploy may still be finishing.</p>}
          {runs && !runs.schema_pending && (
            <>
              <div data-testid="reconcile-last" style={{ fontSize: 14, color: TONE_COLOR[reconcileTone(last?.status)] }}>
                {reconcileSummaryLine(last)}
              </div>
              {last && (
                <div style={{ ...hint, marginTop: 4 }}>
                  {new Date(last.started_at).toLocaleString()}
                  {runs.open_alerts > 0 && <> · {runs.open_alerts} open reconcile alert{runs.open_alerts === 1 ? '' : 's'}</>}
                </div>
              )}
              {last && last.checks.length > 0 && (
                <ul data-testid="reconcile-checks" style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13 }}>
                  {last.checks.map((c) => <li key={c.check}>{checkLine(c)}</li>)}
                </ul>
              )}
              {disabled && <p style={{ ...hint, marginTop: 6 }}>The reconcile is switched off (FF_CODES_RECONCILE).</p>}
            </>
          )}
        </div>
        {writable && (
          <div>
            <button
              type="button"
              data-testid="run-reconcile"
              className="admin-btn admin-btn--secondary admin-btn--md"
              onClick={start}
              disabled={starting || running || disabled || Boolean(runs?.schema_pending)}
            >
              {running ? 'Reconcile running…' : starting ? 'Starting…' : 'Run reconcile now'}
            </button>
          </div>
        )}
      </div>
      {message && <p data-testid="reconcile-message" role="status" style={{ ...hint, marginTop: 8 }}>{message}</p>}
    </AdminCard>
  );
};

export default CodesReconcilePanel;
